import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../../context/AuthContext';
import { useApiEnvironment } from '../../context/ApiEnvironmentContext';
import { useLocale } from '../../context/LocaleContext';
import { getPlazas, createPlaza, updatePlaza, deletePlaza } from '../../api/plazas';
import { getSelectedEnvironment } from '../../utils/apiEnvironment';
import PlazaWorkspace from './PlazaWorkspace';
import { PLAZA_COPY } from './plazaCopy';
import SportsSchedules from './Sports/SportsSchedules';
import { SPORTS_COPY } from './Sports/sportsCopy';
import { useUnsavedChanges } from '../../hooks/useUnsavedChanges';
export function PlazaTarget({ environment, token, user, role, copy }) {
  const client = useQueryClient();
  const attempts = useRef(new Set());
  const writesEnabled = (environment === 'test' || environment === 'prod') && role === 'admin' && !!token;
  const key = ['plazas', environment, user?.id ?? user?.user_id];
  const query = useQuery({ queryKey: key, queryFn: () => getPlazas(token, environment), enabled: !!token, retry: false });
  async function action(operation) {
    if (!writesEnabled || getSelectedEnvironment() !== environment) throw new Error(copy.restricted);
    if (attempts.current.has(operation.operationId)) {
      const current = await getPlazas(token, environment);
      client.setQueryData(key, current);
      if (current.config_revision === operation.operationId) return;
    }
    attempts.current.add(operation.operationId);
    const args = [operation.revision, operation.operationId, token, environment];
    if (operation.kind === 'create') await createPlaza(operation.input, ...args);
    else if (operation.kind === 'delete') await deletePlaza(operation.id, ...args);
    else await updatePlaza(operation.id, operation.input, ...args);
    // Never render an old idempotent replay response as the latest configuration.
    try { client.setQueryData(key, await getPlazas(token, environment)); }
    catch (error) { error.committed = true; throw error; }
  }
  return <PlazaWorkspace collection={query.data} loading={query.isFetching} queryError={query.error} copy={copy} environment={environment.toUpperCase()} writesEnabled={writesEnabled} onRefresh={() => query.refetch()} onAction={action} />;
}
export default function Plaza() {
  const { token, user, role } = useAuth();
  const { apiEnvironment } = useApiEnvironment();
  const { locale } = useLocale();
  const [tab, setTab] = useState('plazas'), [sportsDirty, setSportsDirty] = useState({ dirty: false, locked: false });
  const copy = SPORTS_COPY[locale] || SPORTS_COPY.en;
  const onDirty = useCallback(value => setSportsDirty(value), []);
  useUnsavedChanges(sportsDirty.dirty, sportsDirty.locked, copy.leave);
  useEffect(() => { setSportsDirty({ dirty: false, locked: false }); }, [apiEnvironment, user?.id, user?.user_id]);
  function selectTab(next) { if (next !== tab && !sportsDirty.locked && (!sportsDirty.dirty || window.confirm(copy.leave))) { setTab(next); setSportsDirty({ dirty: false, locked: false }); } }
  return <><div className="plaza-tabs" role="tablist" aria-label={PLAZA_COPY[locale].title}>
    <button id="plaza-management-tab" role="tab" aria-selected={tab === 'plazas'} aria-controls="plaza-management-panel" onClick={() => selectTab('plazas')}>{copy.plazaTab}</button>
    <button id="sports-schedules-tab" role="tab" aria-selected={tab === 'sports'} aria-controls="sports-schedules-panel" onClick={() => selectTab('sports')}>{copy.title}</button>
  </div><div hidden={tab !== 'plazas'} id="plaza-management-panel" role="tabpanel" aria-labelledby="plaza-management-tab"><PlazaTarget key={`${apiEnvironment}:${user?.id ?? user?.user_id}`} environment={apiEnvironment} token={token} user={user} role={role} copy={PLAZA_COPY[locale]} /></div>{tab === 'sports' && <div id="sports-schedules-panel" role="tabpanel" aria-labelledby="sports-schedules-tab"><SportsSchedules key={`${apiEnvironment}:${user?.id ?? user?.user_id}`} environment={apiEnvironment} token={token} role={role} copy={copy} locale={locale} onDirty={onDirty} /></div>}</>;

}
